<!--
 * @Description: 线索公海池
 * @Author: mdb
 * @Date: 2022-07-06 11:24:43
 * @LastEditTime: 2023-03-12 13:52:25
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        rowKey="clueId"
        :row-selection="{ onChange: onSelectRowsChange }"
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        @mask="showMask"
        ref="tableRef"
        tableId="DcCosOFbuPublicClue"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item :label="t('clue.clueType')">
              <CosSelect
                v-model:value="queryForm.clueTypeList"
                :options="clueTypeEnumComputed"
                :placeholder="t('common.pleaseSelect')"
                allowClear
                mode="multiple"
                max-tag-count="responsive"
                show-arrow
              />
            </dc-form-item>
            <dc-form-item :label="t('clue.customerName')">
              <a-input
                v-model:value.trim="queryForm.customerName"
                :maxlength="100"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('clue.channel')">
              <a-input-group compact>
                <ParamCodeCascader
                  type="search"
                  v-model:ids="queryForm.channelList"
                  appId="cos_ofbu"
                  paramCode="ofbu_source_channel"
                  style="width: 33%"
                />
                <a-input
                  v-model:value="queryForm.channelExplain"
                  :maxlength="100"
                  style="width: 67%"
                  :placeholder="t('common.pleaseInput')"
                  allowClear
                />
              </a-input-group>
            </dc-form-item>
            <dc-form-item :label="t('common.createTime')">
              <CosRangePicker
                isOutputBeijingTime
                v-model:startTime="queryForm.startCreateTime"
                v-model:endTime="queryForm.endCreateTime"
              />
            </dc-form-item>
            <dc-form-item :label="t('common.contactMethod')">
              <a-input-group compact>
                <a-select
                  v-model:value="queryForm.contact.type"
                  style="width: 33%"
                  :placeholder="t('common.pleaseSelect')"
                >
                  <a-select-option :value="1">{{ t('common.mobile') }}</a-select-option>
                  <a-select-option :value="2">{{ t('common.email') }}</a-select-option>
                  <a-select-option :value="3">{{ t('common.skype') }}</a-select-option>
                </a-select>
                <a-input
                  v-model:value="queryForm.contact.keyword"
                  :maxlength="100"
                  style="width: 67%"
                  :placeholder="t('common.pleaseInput')"
                />
              </a-input-group>
            </dc-form-item>
            <dc-form-item :label="t('clue.customerRegion')">
              <CustomerAreaCascader type="search" appId="cos_ofbu" v-model:ids="queryForm.customerRegionList" />
            </dc-form-item>
            <dc-form-item :label="t('clue.beforeFollower')">
              <UserSelect v-model:value="queryForm.beforeFollower" :placeholder="t('common.pleaseSelect')" allowClear />
            </dc-form-item>
            <dc-form-item :label="t('clue.clueId')">
              <a-input
                v-model:value="queryForm.clueId"
                :maxlength="100"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <a-button v-permission="'ofbu_public_clue_assign_btn'" type="primary" @click="handleAssignPerson()">{{
            t('btn.assignClue')
          }}</a-button>
          <a-button v-permission="'ofbu_public_clue_receive_btn'" @click="handleReceive()">{{
            t('btn.receiveClue')
          }}</a-button>
          <ExportButton
            v-permission="'ofbu_public_clue_export_btn'"
            appId="cos_ofbu"
            taskType="402"
            :search="queryForm"
            >{{ t('btn.export') }}</ExportButton
          >
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'channel'">
            <ChannelText appId="cos_ofbu" paramCode="ofbu_source_channel" :value="text" :desc="record.channelExplain" />
          </template>
          <template v-if="column.dataIndex === 'customerRegion'">
            <CustomerAreaCascaderText appId="cos_ofbu" :value="text" />
          </template>
          <template v-if="column.dataIndex === 'action'">
            <a-button v-permission="'ofbu_public_clue_assign_btn'" type="link" @click="handleAssignPerson(record)">{{
              t('btn.assignClue')
            }}</a-button>
            <a-button v-permission="'ofbu_public_clue_receive_btn'" type="link" @click="handleReceive(record)">{{
              t('btn.receiveClue')
            }}</a-button>
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>

  <!-- 分配客户负责人弹窗 -->
  <ClueAssign ref="clueAssignRef" @fetch="handleQueryChange('fetch')" />
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted, toRaw } from 'vue'
import { useRoute } from 'vue-router'
import { Modal as AModal } from 'ant-design-vue'
import { message } from '@dc-platform/dc-components'
import ParamCodeCascader from '@/components/CosCascader/components/ParamCodeCascader.vue'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import CustomerAreaCascaderText from '@/components/CustomerAreaCascader/CustomerAreaCascaderText.vue'
import ChannelText from '@/components/CosCascaderText/components/channel.vue'
import ClueAssign from '../components/ClueAssign.vue'
import { clueTypeEnum } from '../dataConfig'
import { genderEnum, linkRoleTypeEnum } from '@/views/overseasSales/commonEnum/dataConfig'
import { listByParamCodes } from '@/api/common'
import { infoData } from '@/api/overseasSales/followRuleSetting'
import { publicClueList, publicClueCollect, salesClueDetail } from '@/api/overseasSales/clueManage'
import type { ParamCodeItem } from '@/typings/common'
import type { FbuPublicClueParams, FbuPublicClueListItem } from '@/typings/api/clueManage'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const route = useRoute()

// 线索类型枚举列表
const clueTypeEnumComputed = computed(() => clueTypeEnum.filter((item) => item.value !== 0))
const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  ofbu_follow_purpose: [], // 拜访目的
  ofbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_ofbu',
    paramCodes: ['ofbu_follow_purpose', 'ofbu_follow_mode']
  }).then((res) => {
    Object.assign(paramCodeData, res.data)
  })
}
const clueRuleList = ref<string[]>([])
const getFollowRuleInfo = async () => {
  const { data } = await infoData({ appId: 'cos_ofbu', configType: 3 })
  clueRuleList.value = (data as any).CLUE_RULE_2.split(',')
}
onMounted(() => {
  getFollowRuleInfo()
  getByParamCodes()
})

// 获取详情联系人信息
const showMask = (record: FbuPublicClueListItem, callBack: (res: any) => void) => {
  salesClueDetail({ clueId: record.clueId as string }).then((res) => {
    const { contact } = res.data
    callBack(contact)
  })
}

const tableRef = ref()
const queryForm = reactive<FbuPublicClueParams>({ contact: { type: 1 } })

const columns = computed(() => [
  {
    title: t('clue.customerName'),
    dataIndex: 'customerName',
    cosType: 'link',
    path: '/ofbu/salesClue/view',
    query: ['clueId', 'isPublicClue=1', `returnPath=${route.fullPath}`],
    fixed: 'left',
    width: 200
  },
  { title: t('clue.clueId'), dataIndex: 'clueId', width: 200 },
  { title: t('clue.channel'), dataIndex: 'channel' },
  { title: t('clue.clueType'), dataIndex: 'clueType', dicData: clueTypeEnum },
  { title: t('clue.customerRegion'), dataIndex: 'customerRegion' },
  { title: t('clue.beforeFollowerName'), dataIndex: 'beforeFollowerName' },
  { title: t('clue.followDesc'), dataIndex: 'followDesc' },
  { title: t('common.contactName'), dataIndex: ['contact', 'name'], cosType: 'mask' },
  { title: t('common.mobile'), dataIndex: ['contact', 'mobile'], cosType: 'mask' },
  { title: t('common.skype'), dataIndex: ['contact', 'skype'], cosType: 'mask' },
  { title: t('common.email'), dataIndex: ['contact', 'email'], cosType: 'mask' },
  { title: t('common.sex'), dataIndex: ['contact', 'sex'], dicData: genderEnum },
  { title: t('common.jobRole'), dataIndex: ['contact', 'jobRole'], dicData: linkRoleTypeEnum },
  { title: t('common.createTime'), dataIndex: 'createTime', width: 190 },
  { title: t('clue.firstReceiveTime'), dataIndex: 'firstReceiveTime', width: 190 },
  { title: t('clue.lastReceiveTime'), dataIndex: 'lastReceiveTime', width: 190 },
  { title: t('clue.firstFollowerName'), dataIndex: 'firstFollowerName' },
  { title: t('clue.lastFollowerName'), dataIndex: 'lastFollowerName' },
  { title: t('clue.transferToPubilcTime'), dataIndex: 'transferTime', width: 190 },
  { title: t('common.remark'), dataIndex: 'remark' },
  {
    title: t('common.action'),
    dataIndex: 'action',
    langWidth: {
      zh: 180,
      en: 260
    },
    fixed: 'right'
  }
])
const getList = (queryForm: FbuPublicClueParams, callBack: (res: any) => void) => {
  publicClueList(queryForm).then((res) => {
    callBack(res)
  })
}
const handleQueryChange = (type: 'search' | 'fetch') => {
  if (type === 'search') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}

const selectedData = ref<FbuPublicClueListItem[]>([])
const onSelectRowsChange = (selectedRowKeys: string[], selectedRows: FbuPublicClueListItem[]) => {
  selectedData.value = selectedRows
}

// 分配客户负责人
const clueAssignRef = ref()
const handleAssignPerson = (row?: FbuPublicClueListItem) => {
  if (!row && !selectedData.value?.length) return message.warning(t('clue.selectClaimClue'))
  const data = row ? [row] : toRaw(selectedData.value)
  clueAssignRef.value?.init(data)
}
// 领取线索
const handleReceive = (row?: FbuPublicClueListItem) => {
  if (!row && !selectedData.value?.length) {
    return message.warning(t('clue.selectDrawClue'))
  }
  const clueIdList: any[] = []
  if (row) {
    clueIdList.push(row.clueId)
  } else {
    selectedData.value.forEach((item) => {
      clueIdList.push(item.clueId)
    })
  }
  AModal.confirm({
    title: t('clue.isSueDrawClue'),
    content: t('clue.returnClueRuleTitle', {
      day: clueRuleList.value[0]
    }),
    okText: t('btn.receive'),
    onOk() {
      publicClueCollect({ clueIdList }).then(() => {
        message.success(t('tips.drawSuccess'))
        handleQueryChange('fetch')
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onCancel() {}
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosOFbuPublicClue' }
</script>

<style scoped></style>
